import { c as _c } from "react/compiler-runtime";
import { Icon } from '@petsmart-ui/sparky';
import { SideDrawer } from '@libs/shared-ui';
import { trackNavigationHeaderClick } from '../../../../../shared';
import { type DrawerNode, isGroupNode } from '../drawer.types';
import { Level, type LevelEntry } from '../shared';
import { OverviewLink } from './overview-link';
import { useNestedLevel } from './use-nested-level';
import { VisualItemsGrid } from './visual-items-grid';
import './nested-level.scss';
type NestedLevelProps = {
  node: DrawerNode;
  rootIsOpen: boolean;
  selectionPath: string[];
  onBack: () => void;
  onCloseAll: () => void;
};
export function NestedLevel(t0) {
  const $ = _c(66);
  const {
    node,
    rootIsOpen,
    selectionPath,
    onBack,
    onCloseAll
  } = t0;
  let t1;
  if ($[0] !== onBack || $[1] !== onCloseAll || $[2] !== rootIsOpen) {
    t1 = {
      rootIsOpen,
      onBack,
      onCloseAll
    };
    $[0] = onBack;
    $[1] = onCloseAll;
    $[2] = rootIsOpen;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    activeNode,
    closeChild,
    handleBack,
    handleOpenChange,
    handleOpenChangeComplete,
    isOpen,
    openNode
  } = useNestedLevel(t1);
  let t2;
  if ($[4] !== node) {
    t2 = isGroupNode(node);
    $[4] = node;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isGroup = t2;
  const overviewLink = isGroup ? node.overviewLink : node.kind === "panel" ? node.panel.overviewLink : undefined;
  let t3;
  if ($[6] !== onCloseAll || $[7] !== selectionPath) {
    t3 = function trackNavLink(href, label) {
      trackNavigationHeaderClick({
        selection: [...selectionPath, label],
        linkUrl: href,
        navSection: "main nav"
      });
      onCloseAll();
    };
    $[6] = onCloseAll;
    $[7] = selectionPath;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const trackNavLink = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Icon name="chevron-left" size="lg" aria-hidden="true" />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== handleBack) {
    t5 = <button type="button" aria-label="Back" className="mobile-menu-drawer__back" onClick={handleBack}>{t4}</button>;
    $[10] = handleBack;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== node.title) {
    t6 = <SideDrawer.Title className="mobile-menu-drawer__level-title">{node.title}</SideDrawer.Title>;
    $[12] = node.title;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <SideDrawer.Header className="mobile-menu-drawer__header mobile-menu-drawer__header--nested">{t5}{t6}</SideDrawer.Header>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== overviewLink || $[18] !== trackNavLink) {
    t8 = overviewLink && <OverviewLink link={overviewLink} onSelect={() => trackNavLink(overviewLink.href, overviewLink.label)} />;
    $[17] = overviewLink;
    $[18] = trackNavLink;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== isGroup || $[21] !== node.links || $[22] !== node.title || $[23] !== trackNavLink) {
    t9 = isGroup && <Level label={node.title} entries={node.links.map(link => ({
      href: link.href,
      id: `${link.href}::${link.label}`,
      label: link.label,
      onSelect: () => trackNavLink(link.href, link.label),
      openInNewTab: link.openInNewTab
    }))} />;
    $[20] = isGroup;
    $[21] = node.links;
    $[22] = node.title;
    $[23] = trackNavLink;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== isGroup || $[26] !== node.kind || $[27] !== node.sidebarItems || $[28] !== node.title || $[29] !== openNode) {
    t10 = !isGroup && node.kind === "sidebar" && <Level label={node.title} entries={node.sidebarItems.map(item => ({
      id: item.id,
      label: item.label,
      onSelect: trigger => openNode({
        id: item.id,
        kind: "panel",
        panel: item.panel,
        title: item.label
      }, trigger)
    }))} />;
    $[25] = isGroup;
    $[26] = node.kind;
    $[27] = node.sidebarItems;
    $[28] = node.title;
    $[29] = openNode;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== isGroup || $[32] !== node.kind || $[33] !== node.panel || $[34] !== node.title || $[35] !== openNode) {
    t11 = !isGroup && node.kind === "panel" && node.panel.content.kind === "columns" && <Level label={node.title} entries={node.panel.content.columns.flat().map((group, index) => ({
      id: `${group.title}-${index}`,
      label: group.title,
      onSelect: trigger_0 => openNode(group, trigger_0)
    }))} />;
    $[31] = isGroup;
    $[32] = node.kind;
    $[33] = node.panel;
    $[34] = node.title;
    $[35] = openNode;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== isGroup || $[38] !== node.kind || $[39] !== node.panel || $[40] !== node.title || $[41] !== onCloseAll || $[42] !== selectionPath || $[43] !== trackNavLink) {
    t12 = !isGroup && node.kind === "panel" && node.panel.content.kind === "visual" && (node.panel.content.layout === "imageOnly" ? <VisualItemsGrid items={node.panel.content.items} selectionPath={selectionPath} onLinkSelect={onCloseAll} /> : <Level label={node.title} entries={node.panel.content.items.map(item_0 => ({
      href: item_0.href,
      id: `${item_0.href}::${item_0.title ?? item_0.label}`,
      label: item_0.title ?? item_0.label,
      onSelect: () => trackNavLink(item_0.href, item_0.title ?? item_0.label),
      openInNewTab: item_0.openInNewTab
    }))} />);
    $[37] = isGroup;
    $[38] = node.kind;
    $[39] = node.panel;
    $[40] = node.title;
    $[41] = onCloseAll;
    $[42] = selectionPath;
    $[43] = trackNavLink;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t8 || $[49] !== t9) {
    t13 = <SideDrawer.Body>{t8}{t9}{t10}{t11}{t12}</SideDrawer.Body>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t8;
    $[49] = t9;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== handleOpenChange || $[52] !== handleOpenChangeComplete || $[53] !== isOpen || $[54] !== t13 || $[55] !== t7) {
    t14 = <SideDrawer className="mobile-menu-drawer" isOpen={isOpen} showBackdrop={false} onOpenChange={handleOpenChange} onOpenChangeComplete={handleOpenChangeComplete}>{t7}{t13}</SideDrawer>;
    $[51] = handleOpenChange;
    $[52] = handleOpenChangeComplete;
    $[53] = isOpen;
    $[54] = t13;
    $[55] = t7;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== activeNode || $[58] !== closeChild || $[59] !== onCloseAll || $[60] !== rootIsOpen || $[61] !== selectionPath) {
    t15 = activeNode && <NestedLevel node={activeNode} rootIsOpen={rootIsOpen} selectionPath={[...selectionPath, activeNode.title]} onBack={closeChild} onCloseAll={onCloseAll} />;
    $[57] = activeNode;
    $[58] = closeChild;
    $[59] = onCloseAll;
    $[60] = rootIsOpen;
    $[61] = selectionPath;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  let t16;
  if ($[63] !== t14 || $[64] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[63] = t14;
    $[64] = t15;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  return t16;
}